import React, { useMemo } from 'react';
import { Loader2, ExternalLink, Globe } from 'lucide-react';
import { CoinDetail } from '../../hooks/useCoinDetail';
import { useCurrency } from '../../context/CurrencyContext';
import { formatPercent } from '../../utils/formatters';

/**
 * "Coin's Info" tab for the Coin Detail page. Pure presentation over the data
 * already fetched by useCoinDetail (GET /coins/{id}, 5-min cached) — no new
 * network call. Crypto-only; the caller gates this behind assetType === 'crypto'.
 *
 * The CoinGecko description is HTML-ish (contains <a>, <b>, entities). We extract
 * plain text with DOMParser (which does NOT execute scripts or load resources) and
 * render it as escaped text, so there is no XSS surface.
 */

interface Props {
  detail: CoinDetail | null;
  loading: boolean;
  name: string;
  symbol: string;
  fallbackImage?: string;
}

// ── Formatting helpers ────────────────────────────────────────────────────────

function currencySymbol(code: string): string {
  try {
    return (0)
      .toLocaleString('en-US', { style: 'currency', currency: code, minimumFractionDigits: 0, maximumFractionDigits: 0 })
      .replace(/\d/g, '')
      .trim();
  } catch {
    return `${code} `;
  }
}

// Compact monetary value in the selected display currency (T / B / M / K).
function compactCurrency(usd: number, rate: number, code: string): string {
  if (!usd || !isFinite(usd)) return '—';
  const v = usd * rate;
  const sym = currencySymbol(code);
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (abs >= 1e12) return `${sign}${sym}${(abs / 1e12).toFixed(2)}T`;
  if (abs >= 1e9)  return `${sign}${sym}${(abs / 1e9).toFixed(2)}B`;
  if (abs >= 1e6)  return `${sign}${sym}${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3)  return `${sign}${sym}${(abs / 1e3).toFixed(2)}K`;
  return `${sign}${sym}${abs.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function formatSupply(n: number | null, symbol: string): string {
  if (!n) return '—';
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B ${symbol}`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M ${symbol}`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(2)}K ${symbol}`;
  return `${n.toLocaleString('en-US')} ${symbol}`;
}

function fmtIsoDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Extract safe plain text from CoinGecko's HTML-ish description. DOMParser does
// not run scripts, and we only read textContent, so this cannot inject markup.
function stripHtml(html: string): string {
  if (!html) return '';
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return (doc.body.textContent || '').trim();
  } catch {
    return html.replace(/<[^>]*>/g, '').trim();
  }
}

// ── Small presentational pieces ───────────────────────────────────────────────

// Boxless on mobile (< md): `className` carries the per-cell divider borders that
// quarter the grid. Boxed card at ≥ md (the md: classes fully override the mobile
// dividers back into a solid card, matching the desktop look).
function Stat({ label, value, sub, className }: { label: string; value: React.ReactNode; sub?: React.ReactNode; className?: string }) {
  return (
    <div className={`py-3 md:p-3.5 md:rounded-xl md:border md:border-slate-700/60 md:bg-slate-800/30 ${className ?? ''}`}>
      <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</p>
      <p className="text-sm font-semibold text-slate-100 num">{value}</p>
      {sub && <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}

function LinkChip({ href, icon, label }: { href: string; icon: React.ReactNode; label: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-lg border border-slate-700 bg-slate-800/40 text-slate-200 hover:bg-slate-700/50 hover:border-slate-500 transition-colors"
    >
      {icon}
      <span className="truncate max-w-[160px]">{label}</span>
      <ExternalLink className="w-3 h-3 text-slate-500 flex-shrink-0" />
    </a>
  );
}

export function CoinInfo({ detail, loading, name, symbol, fallbackImage }: Props) {
  const { fmt, rate, currency } = useCurrency();
  const description = useMemo(() => (detail ? stripHtml(detail.description) : ''), [detail]);

  if (loading && !detail) {
    return (
      <div className="flex items-center gap-2 text-slate-500 text-sm py-10 justify-center">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading coin info…
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="py-12 text-center text-sm text-slate-600">
        Coin info is unavailable right now.
      </div>
    );
  }

  const symU = symbol.toUpperCase();
  const logo = detail.image || fallbackImage || '';
  const paragraphs = description ? description.split(/\n+/).map((s) => s.trim()).filter(Boolean) : [];
  const categories = detail.categories.slice(0, 6);

  // Market/supply stats — each hidden when the underlying field is missing.
  const stats: { label: string; value: React.ReactNode; sub?: React.ReactNode }[] = [];
  if (detail.marketCap > 0)        stats.push({ label: 'Market Cap', value: compactCurrency(detail.marketCap, rate, currency.code) });
  if (detail.volume24h > 0)        stats.push({ label: '24h Volume', value: compactCurrency(detail.volume24h, rate, currency.code) });
  if (detail.high24h > 0)          stats.push({ label: '24h High', value: <span className="text-emerald-400">{fmt(detail.high24h)}</span> });
  if (detail.low24h > 0)           stats.push({ label: '24h Low', value: <span className="text-red-400">{fmt(detail.low24h)}</span> });
  if (detail.circulatingSupply > 0) stats.push({ label: 'Circulating Supply', value: formatSupply(detail.circulatingSupply, symU) });
  if (detail.totalSupply)          stats.push({ label: 'Total Supply', value: formatSupply(detail.totalSupply, symU) });
  stats.push({ label: 'Max Supply', value: detail.maxSupply ? formatSupply(detail.maxSupply, symU) : '∞' });
  if (detail.ath > 0) {
    stats.push({
      label: 'All-Time High',
      value: <span className="text-emerald-400">{fmt(detail.ath)}</span>,
      sub: (
        <>
          {detail.athDate && fmtIsoDate(detail.athDate)}
          {detail.athChangePercent < 0 && (
            <span className="text-red-400"> · {formatPercent(detail.athChangePercent)} from ATH</span>
          )}
        </>
      ),
    });
  }
  if (detail.atl > 0) {
    stats.push({
      label: 'All-Time Low',
      value: fmt(detail.atl),
      sub: detail.atlDate ? fmtIsoDate(detail.atlDate) : undefined,
    });
  }
  if (detail.genesisDate) stats.push({ label: 'Genesis Date', value: fmtIsoDate(detail.genesisDate) });

  // External links — only the official homepage (hidden when missing).
  const links: { href: string; icon: React.ReactNode; label: string }[] = [];
  if (detail.homepage[0])  links.push({ href: detail.homepage[0], icon: <Globe className="w-3.5 h-3.5" />, label: 'Website' });

  return (
    <div className="space-y-6">
      {/* Identity */}
      <div className="flex items-center gap-3 flex-wrap">
        {logo && (
          <img
            src={logo}
            alt={name}
            className="w-11 h-11 rounded-full flex-shrink-0 bg-slate-800"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        )}
        <div className="min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-lg font-bold text-slate-100">{name}</span>
            <span className="text-sm text-slate-500">{symU}</span>
            {detail.marketCapRank > 0 && (
              <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-800 border border-slate-700 text-slate-300">
                Rank #{detail.marketCapRank}
              </span>
            )}
          </div>
          {categories.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap mt-2">
              {categories.map((c) => (
                <span
                  key={c}
                  className="text-[10px] font-medium px-2 py-0.5 rounded-md bg-[rgba(212,180,131,0.1)] border border-[rgba(212,180,131,0.28)] text-[#b89968]"
                >
                  {c}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Description */}
      {paragraphs.length > 0 && (
        <div>
          <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">About {name}</p>
          <div className="space-y-2">
            {paragraphs.map((p, i) => (
              <p key={i} className="text-[13px] leading-relaxed text-slate-400">{p}</p>
            ))}
          </div>
        </div>
      )}

      {/* Market / supply stats — boxless divided grid on mobile, boxed cards ≥ md */}
      {stats.length > 0 && (
        <div>
          <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Market Stats</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-0 md:gap-3">
            {stats.map((s, i) => {
              // Mobile is always 2 columns: quarter the grid with thin dividers
              // (right border between the two columns, bottom border between rows).
              const col = i % 2;
              const numRows = Math.ceil(stats.length / 2);
              const hasRightNeighbor = col === 0 && i + 1 < stats.length;
              const notLastRow = Math.floor(i / 2) < numRows - 1;
              const cell = [
                col === 0 ? 'pr-3' : 'pl-3',
                hasRightNeighbor ? 'border-r border-slate-700/40' : '',
                notLastRow ? 'border-b border-slate-700/40' : '',
              ].filter(Boolean).join(' ');
              return <Stat key={s.label} label={s.label} value={s.value} sub={s.sub} className={cell} />;
            })}
          </div>
        </div>
      )}

      {/* Links */}
      {links.length > 0 && (
        <div>
          <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Links</p>
          <div className="flex flex-wrap gap-2">
            {links.map((l, i) => (
              <LinkChip key={`${l.label}-${i}`} href={l.href} icon={l.icon} label={l.label} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
